import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Components from "../../../components";
import { useForm, useDropDownSearch } from "../../../hooks";
import { FormContainer } from "../Shared/FormContainer";
import { mergeObjects } from "../../../shared/helper";
import {
  useCreateVManModelMutation,
  useUpdateVManModelMutation,
  useGetVManModelQuery,
  useGetClientsQuery,
  useGetVMansQuery,
} from "../services";
import { vManModelFormValidation } from "../validations";

export const VManModelForm = ({ idToEdit = null, closeForm }) => {
  const dropDownParams = { per_page: 1000, order_by: "name" };
  const [initialValues, setInitialValues] = useState({
    name: "",
    client_id: "",
    vehicle_manufacturer_id: "",
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: vManModelFormValidation,
    onSubmit: (values) => handleFormSubmit(values),
  });

  // Get the clients list using Dropdown Search
  const {
    data: clients,
    setSearchKey: setClientSearch,
    isSuccess: isClientsSuccess,
  } = useDropDownSearch({
    useData: useGetClientsQuery,
    simpleSearchKey: "name_or_email_cont",
    selectedIds: formik.values.client_id,
    additionalParams: { ...dropDownParams },
  });

  // Get the vehicle manufacturers list using Dropdown Search
  const {
    data: vManufacturers = { data: [] },
    setSearchKey: setVManSearch,
    isSuccess: isvManufacturersSuccess,
  } = useDropDownSearch({
    useData: useGetVMansQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.vehicle_manufacturer_id,
    additionalParams: formik.values.client_id
      ? { ...dropDownParams, client_id: formik.values.client_id }
      : { ...dropDownParams, only_master: true },
  });

  const { data } = useGetVManModelQuery({ id: idToEdit }, { skip: !idToEdit });

  const { create: createVManModel, update: updateVManModel } = useForm({
    createMutation: useCreateVManModelMutation,
    updateMutation: useUpdateVManModelMutation,
    closeForm,
    setError: formik.setErrors,
  });

  useEffect(() => {
    if (idToEdit && data) {
      setInitialValues(mergeObjects(initialValues, data));
    }
  }, [data]);

  const handleFormSubmit = (formData) => {
    if (idToEdit) {
      updateVManModel({ id: idToEdit, formData });
    } else {
      createVManModel({ formData });
    }
  };

  return (
    <FormContainer
      resourceName="Manufacturer Model"
      closeForm={closeForm}
      idToEdit={idToEdit}
      handleFormSubmit={formik.handleSubmit}
      disabled={!!idToEdit}
    >
      <Components.QICustomSelect
        label="Client"
        {...formik.getFieldProps("client_id")}
        error={formik.touched.client_id && formik.errors.client_id}
        onChange={(value) => {
          formik.setFieldValue("client_id", value);
          setClientSearch("");
        }}
        onSearch={setClientSearch}
        labelClassName="client"
        disabled={!!idToEdit}
      >
        <li value="">All</li>
        {isClientsSuccess &&
          clients.data.map((client) => (
            <li key={client.id} value={client.id}>
              {client.name}
            </li>
          ))}
      </Components.QICustomSelect>
      <Components.QICustomSelect
        label="Manufacturer"
        {...formik.getFieldProps("vehicle_manufacturer_id")}
        error={formik.touched.vehicle_manufacturer_id && formik.errors.vehicle_manufacturer_id}
        onChange={(value) => {
          formik.setFieldValue("vehicle_manufacturer_id", value);
          setVManSearch("");
        }}
        onSearch={setVManSearch}
        labelClassName="manufacturer"
        disabled={!!idToEdit}
      >
        <li value="">Select</li>
        {isvManufacturersSuccess &&
          vManufacturers.data.map((vManufacturer) => (
            <li key={vManufacturer.id} value={vManufacturer.id}>
              {vManufacturer.name}
            </li>
          ))}
      </Components.QICustomSelect>
      <Components.QIInput
        label="Name"
        placeholder="Vehicle model name"
        {...formik.getFieldProps("name")}
        error={formik.touched.name && formik.errors.name}
        disabled={!!idToEdit}
      />
    </FormContainer>
  );
};
